// Appointments are kept in this browser (IndexedDB) and only changes are downloaded:
// rows whose changed_at is at or after the newest one already cached.
import { config, mode } from '../config';
import { planIngest, snapshotStats, toUpsertRow } from '../domain/ingest';
import type { Appointment } from '../domain/types';
import { db } from './db';
import { kvDel, kvGet, kvSet } from './kv';

interface Cache {
  rows: Record<string, Appointment>;
  last: string | null;
}
let cache: Cache | null = null;
let cacheUser = '';

const cacheKey = (userId: string) => 'appts:' + (mode === 'supabase' ? config.supabaseUrl + ':' + userId : 'local');

export async function loadAppointments(userId: string, onProgress?: (n: number) => void): Promise<Appointment[]> {
  if (db.local) {
    const rows = await db.fetchAppointments(null);
    return rows.map((r) => r.raw);
  }
  if (!cache || cacheUser !== userId) {
    cache = (await kvGet<Cache>(cacheKey(userId))) || { rows: {}, last: null };
    cacheUser = userId;
  }
  const c = cache;
  const fresh = await db.fetchAppointments(c.last, onProgress);
  if (fresh.length) {
    fresh.forEach((r) => {
      c.rows[r.id] = r.raw;
      if (!c.last || r.changed_at > c.last) c.last = r.changed_at;
    });
    await kvSet(cacheKey(userId), c);
  }
  return Object.values(c.rows);
}

export async function clearAppointmentCache(userId: string): Promise<void> {
  cache = null;
  await kvDel(cacheKey(userId));
}

export interface IngestResult {
  file: string;
  total: number;
  nw: number;
  ch: number;
  same: number;
  snapshot?: number;
}

/**
 * Saves an export: compares it with what is saved, inserts a snapshot (figures on the merged,
 * cleaned set) and upserts only new or changed rows, 300 at a time. Secrets are stripped.
 */
export async function ingestExport(
  userId: string,
  arr: Appointment[],
  fileName: string,
  say: (step: { kind: 'compare' } | { kind: 'upload'; done: number; total: number }) => void,
): Promise<IngestResult> {
  say({ kind: 'compare' });
  const current = await loadAppointments(userId);
  const plan = planIngest(current, arr);
  const res: IngestResult = { file: fileName, total: plan.total, nw: plan.nNew, ch: plan.nChanged, same: plan.same };
  if (!plan.changed.length) return res;
  const snapId = await db.insertSnapshot({
    file_name: fileName || '', total_rows: plan.total, new_rows: plan.nNew, changed_rows: plan.nChanged, stats: snapshotStats(plan.merged),
  });
  const B = 300;
  for (let i = 0; i < plan.changed.length; i += B) {
    say({ kind: 'upload', done: Math.min(i + B, plan.changed.length), total: plan.changed.length });
    await db.upsertAppointments(plan.changed.slice(i, i + B).map((a) => toUpsertRow(a, snapId)));
  }
  await loadAppointments(userId);
  res.snapshot = snapId;
  return res;
}
